import { QRCodeSVG } from 'qrcode.react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useLocalizedTime from '../../hooks/useLocalizedTime';
import useQrCode from '../../hooks/useDisplayQrCode';
import { getByteLength, getValueByLanguage, kanaFullToHalf } from '../../utils';
import { DISTRIBUTION_TYPE, LANGUAGES } from '../../utils/Constants';
import DisplayCustomNumbers from '../DisplayCustomNumbers/DisplayCustomNumbers';
import SmallLoading from '../SmallLoading/SmallLoading';
import ScrollingText from '../ScrollingText/ScrollingText';
import FujiModal from '../fuji-modal/FujiModal';

import styles from './styles.module.css';
import { DistributionQrCode, OrderTicket, MultilingualType } from 'types';
import { RootState } from 'redux/reducers/rootReducer';

interface QrCodeDisplayProps {
    orderTicket: OrderTicket;
    /** チケットが使用済みになった、もしくはチケットの有効期間が切れた場合のリダイレクトURL */
    redirectUrl: string;
}

const QrCodeDisplay: React.FC<QrCodeDisplayProps> = ({ orderTicket, redirectUrl }) => {
    const [size, setSize] = useState(() => {
        const innerWidth = window.innerWidth * 0.5;
        const innerHeight = window.innerHeight * 0.5;
        return Math.min(innerWidth, innerHeight, 200);
    });
    const [ticketNameFontSize, setTicketNameFontSize] = useState(16);
    const [centerFontSize, setCenterFontSize] = useState(16);
    const currentTheme = useSelector((state: RootState) => state.currentTheme);
    const activeThemeIndex = useSelector((state: RootState) => state.activeThemeIndex);
    const { i18n, t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { qrCode, fetchQrCode, isLoading, isOpenConfirmQrCodeModal, closeConfirmQrCodeModal } = useQrCode();

    const reservationInfo = orderTicket?.orderUsageTickets?.[0]?.reservationInfo;

    const mainRef = useRef<HTMLDivElement>(null);

    const l = useCallback(
        (text?: MultilingualType | null) => {
            return getValueByLanguage(text, activeThemeIndex === 0 ? i18n.language : 'ja');
        },
        [activeThemeIndex, i18n.language],
    );
    /**
     * QRコードを取得
     */
    useEffect(() => {
        const { remainingOrderDistribution, orderTicketId, distributionType } = orderTicket ?? {};
        const orderDistributionId = (orderTicket as unknown as DistributionQrCode).orderDistributionId;
        const { remainingOrderDistributionId } = remainingOrderDistribution ?? {};
        let requestParams: {
            remainingOrderDistributionId?: string | null;
            orderDistributionId?: string | null;
            orderTicketId?: string | null;
        } = { remainingOrderDistributionId };
        if (orderDistributionId) {
            requestParams = { orderDistributionId };
        } else if (distributionType === DISTRIBUTION_TYPE.DISABLED) {
            requestParams = { orderTicketId };
        }
        void fetchQrCode(requestParams);
    }, [orderTicket]);
    /**
     * QRコードのサイズをリセットする関数
     */
    useEffect(() => {
        const resetSize = () => {
            const innerWidth = window.innerWidth * 0.5;
            const innerHeight = window.innerHeight * 0.5;
            setSize(Math.min(innerWidth, innerHeight, 200));
        };

        let timer: NodeJS.Timeout;
        const delayTrigger = () => {
            clearTimeout(timer);
            timer = setTimeout(resetSize, 100); // 100msだけ画面のサイズ変更が起きない場合に、QRコードのサイズを変更します。
        };
        resetSize();
        window.addEventListener('resize', delayTrigger);
        window.addEventListener('orientationchange', delayTrigger);
        return () => {
            window.removeEventListener('resize', delayTrigger);
            window.removeEventListener('orientationchange', delayTrigger);
        };
    }, []);
    /**
     * チケット名のサイズをリセットする関数
     */
    useEffect(() => {
        const resetSize = () => {
            if (mainRef.current) {
                const { height } = mainRef.current.getBoundingClientRect();

                const ticketName = l(orderTicket?.ticketNameShort) || l(orderTicket?.ticketName);
                setTicketNameFontSize(Math.min(Math.max(height / getByteLength(ticketName), 16), 30));
                setCenterFontSize(
                    Math.min(Math.max((height * 0.8) / getByteLength(l(reservationInfo?.stockPartitionName)), 16), 30),
                );
            }
        };

        let timer: NodeJS.Timeout;
        const delayTrigger = () => {
            clearTimeout(timer);
            timer = setTimeout(resetSize, 100); // 100msだけ画面のサイズ変更が起きない場合に、くるくるをリサイズします。
        };
        resetSize();
        window.addEventListener('resize', delayTrigger);
        window.addEventListener('orientationchange', delayTrigger);
        return () => {
            window.removeEventListener('resize', delayTrigger);
            window.removeEventListener('orientationchange', delayTrigger);
        };
    }, [mainRef]);

    const fontFamily = useMemo(() => {
        return LANGUAGES.find((item) => (activeThemeIndex === 0 ? item.key === i18n.language : item.key === 'ja'))
            ?.fontFamily;
    }, [activeThemeIndex, i18n.language]);

    const { ticketName } = useMemo(() => {
        const ticketName = l(orderTicket?.ticketNameShort) || l(orderTicket?.ticketName);
        return { ticketName };
    }, [l, orderTicket]);

    const representativeName = useMemo(() => {
        const representativeName =
            (reservationInfo as any)?.representativeName ?? reservationInfo?.userForm?.representativeName;
        if (representativeName) {
            return kanaFullToHalf(representativeName);
        }
        return '';
    }, [l, reservationInfo]);

    const formatType = orderTicket?.isDisplayReserveOnTime === false ? 'll' : 'lll';
    return (
        <div className={styles.wrapper}>
            <FujiModal
                isOpen={isOpenConfirmQrCodeModal}
                onSubmit={() => closeConfirmQrCodeModal(redirectUrl)}
                txtSubmit={'OK'}
                body={t('ticket-display.qr-authentication-completed')}
            />
            <SmallLoading show={isLoading} content={t('loading.small-bought-ticket') ?? ''} />
            <div className={styles.topLayout}>
                {qrCode && (
                    <QRCodeSVG
                        value={qrCode}
                        size={size}
                        level={'H'}
                        includeMargin={true}
                        bgColor={'#FFFFFF'}
                        fgColor={'#000000'}
                    />
                )}
            </div>
            <div className={styles.mainLayout} ref={mainRef}>
                <div className={styles.textArea} style={{ color: currentTheme?.colorFontTop, fontFamily }}>
                    <h2 className={styles.operatorName}>{l(orderTicket.operatorName)}</h2>
                    <h1 className={styles.ticketName} style={{ fontSize: ticketNameFontSize }}>
                        {ticketName}
                    </h1>
                    {orderTicket.subtitle && (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <ScrollingText width={'90%'} delay={1000} pause={1000} canAnimate={true}>
                                {l(orderTicket.subtitle)}
                            </ScrollingText>
                        </div>
                    )}
                    <div style={{ color: currentTheme?.colorFontCenter, fontFamily }}>
                        {reservationInfo?.stockPartitionName && (
                            <h2 className={styles.centerName} style={{ fontSize: centerFontSize }}>
                                {l(reservationInfo?.stockPartitionName)}
                            </h2>
                        )}
                        <div>{formatTime(reservationInfo?.reserveOn, formatType)}</div>
                    </div>
                </div>
                <div className={styles.infoArea}>
                    {representativeName && (
                        <div
                            style={{
                                color: currentTheme.colorFontBottom,
                                fontSize: size * 0.125,
                                textOverflow: 'clip',
                                overflow: 'hidden',
                                paddingLeft: '10px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {representativeName}
                        </div>
                    )}

                    <DisplayCustomNumbers
                        orderTicket={orderTicket}
                        circleSize={size * 0.125}
                        textColor={currentTheme.colorFontBottom}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(QrCodeDisplay);
